import {
  getResearchDictionaries,
  listResearchCompanies,
  listResearchMaterials,
  listResearchTopics,
  type ResearchCompany,
  type ResearchMaterial,
  type ResearchPage,
  type ResearchTopic,
} from '@/services/bff/research.api';
import type { Company, FollowStatus, Material, Topic } from './types';

const PAGE_SIZE = 100;

async function fetchAllPages<T>(
  fetchPage: (page: number, pageSize: number) => Promise<ResearchPage<T>>,
): Promise<T[]> {
  const items: T[] = [];
  for (let page = 1; ; page += 1) {
    const result = await fetchPage(page, PAGE_SIZE);
    const batch = result?.items ?? [];
    items.push(...batch);
    const total = result?.total;
    if (
      !batch.length ||
      (total !== undefined ? items.length >= total : batch.length < PAGE_SIZE)
    ) {
      break;
    }
  }
  return items;
}

const toTopic = (item: ResearchTopic): Topic => ({
  id: item.uuid,
  name: item.name,
  type: item.type || '其他',
  industry: item.industry || '',
  directions: item.directions ?? [],
  owner: item.owner || '',
  status: item.status || '研究中',
  attention: item.attention ?? 3,
  updatedAt: String(item.updatedAt ?? ''),
  summary: item.summary ?? '',
  currentConclusion: item.currentConclusion ?? '',
  companyIds: item.companyIds ?? [],
  materialIds: item.materialIds ?? [],
  factIds: [],
  viewpointIds: [],
});

const toMaterial = (item: ResearchMaterial): Material => ({
  id: item.uuid,
  title: item.title,
  type: item.type || '研报',
  source: item.source || '',
  author: item.author || '',
  summary: item.summary ?? '',
  topicIds: item.topicIds ?? [],
  companyIds: item.companyIds ?? [],
  publishedAt: item.publishedAt ?? '',
  updatedAt: String(item.updatedAt ?? ''),
  favorite: Boolean(item.favorite),
});

const toCompany = (item: ResearchCompany): Company => ({
  id: item.uuid,
  name: item.name,
  shortName: item.shortName || item.name.slice(0, 6),
  industry: item.industry || '',
  region: item.region || '',
  round: item.round || '',
  source: item.source || '',
  followStatus: (item.followStatus || '普通关注') as FollowStatus,
  owner: item.owner || '',
  topicIds: item.topicIds ?? [],
  latestEvent: item.latestEvent || '',
  judgment: item.judgment ?? '',
  leadUuid: item.leadUuid ?? null,
});

export interface HydratedResearchData {
  topics: Record<string, Topic>;
  companies: Record<string, Company>;
  materials: Record<string, Material>;
  dictionaries?: Partial<Record<string, string[]>>;
}

/**
 * 拉取研究域全部记录（专题 / 资料 / 企业），
 * 映射为会话状态使用的 EntityMap。空租户返回空映射。
 */
export async function fetchResearchState(): Promise<HydratedResearchData> {
  const [topics, materials, companies, dictionaryResult] = await Promise.all([
    fetchAllPages((page, pageSize) => listResearchTopics({ page, pageSize })),
    fetchAllPages((page, pageSize) =>
      listResearchMaterials({ page, pageSize }),
    ),
    fetchAllPages((page, pageSize) =>
      listResearchCompanies({ page, pageSize }),
    ),
    getResearchDictionaries().catch(() => null),
  ]);
  const dictionaries = dictionaryResult
    ? Object.fromEntries(
        Object.entries(dictionaryResult.dictionaries).map(([key, item]) => [
          key,
          item.values,
        ]),
      )
    : undefined;
  return {
    topics: Object.fromEntries(topics.map(item => [item.uuid, toTopic(item)])),
    materials: Object.fromEntries(
      materials.map(item => [item.uuid, toMaterial(item)]),
    ),
    companies: Object.fromEntries(
      companies.map(item => [item.uuid, toCompany(item)]),
    ),
    dictionaries,
  };
}
